import { KEYCLOAK_BUILTIN_CLIENTS } from '../../constants/auth-methods';
import { fetchRoleUserCount, type KeycloakClient } from '../../api/keycloak-admin';

/** System clients that cannot be managed from the admin UI. */
const PROTECTED_CLIENTS: ReadonlySet<string> = new Set(['vertex-hub']);

/** Filter out Keycloak built-in clients and protected system clients. */
export function filterAppClients(clients: KeycloakClient[]): KeycloakClient[] {
  return clients.filter(c =>
    !KEYCLOAK_BUILTIN_CLIENTS.has(c.clientId)
    && !c.clientId.startsWith('security-')
    && !PROTECTED_CLIENTS.has(c.clientId),
  );
}

/**
 * Fetch the user count for each app client serially. Any individual
 * failure resolves to 0 so one bad role doesn't break the whole map.
 */
export async function loadUserCounts(
  token: string,
  clients: KeycloakClient[],
  setter: (counts: Record<string, number>) => void,
): Promise<void> {
  const counts: Record<string, number> = {};
  for (const client of clients) {
    counts[client.clientId] = await fetchRoleUserCount(token, `app:${client.clientId}`).catch(() => 0);
  }
  setter(counts);
}

/**
 * Convert a human-readable app name into a Keycloak-safe `clientId`.
 * Lowercase, dashes for spaces, strip anything that isn't `[a-z0-9-]`.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .split(' ')
    .join('-')
    .split('')
    .filter(c => c === '-' || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9'))
    .join('');
}

/**
 * Normalize an operator-typed app URL into an absolute URL Keycloak accepts
 * for a client's `rootUrl`/`baseUrl`. Trims whitespace, prepends `https://`
 * when no scheme is present, and validates the result. Returns the normalized
 * origin (no trailing slash) or `null` when the input cannot form a valid URL.
 */
export function normalizeAppUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname) return null;
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return null;
  }
}

/**
 * Heuristic: did an error come from Keycloak being briefly unreachable
 * (e.g. `docker restart` during theme publish)? Network errors + the
 * 5xx family all count so the UI can show a softer "restarting" banner.
 */
export function isKeycloakRestartError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const message = err.message;
  if (message.includes('503') || message.includes('502') || message.includes('504')) return true;
  if (message.includes('ECONNREFUSED') || message.includes('ECONNRESET')) return true;
  if (message.includes('Keycloak is')) return true;
  return false;
}
